import {
  resolveUserSystemSettings,
  saveUserSystemSettings,
} from '@/core/helpers/userSystemSettings';
import {
  clampYearToWorkingYears,
  getFallbackWorkingYears,
} from '@/core/helpers/working-years';
import type { UserSystemSettings } from '@/core/interfaces/UserSystemSettings';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
// used as @Module/@Action/@Mutation decorators
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

@Module
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default class UserSystemSettingsModule extends VuexModule {
  year: string = new Date().getFullYear().toString();
  idDonVi: number | null = null;
  perpage = 50;
  tenDonVi = '';

  get getSystemIdDonVi(): number | null {
    return this.idDonVi;
  }

  get getSystemPerPage(): number {
    return this.perpage;
  }

  get getSystemTenDonVi(): string {
    return this.tenDonVi;
  }

  @Mutation
  [Mutations.SET_USER_SYSTEM_SETTINGS](settings: UserSystemSettings) {
    this.year = settings.year;
    this.idDonVi = settings.idDonVi;
    this.perpage = settings.perpage;
  }

  @Mutation
  [Mutations.SET_USER_SYSTEM_TEN_DON_VI](tenDonVi: string) {
    this.tenDonVi = tenDonVi;
  }

  @Action
  [Actions.INIT_USER_SYSTEM_SETTINGS](payload?: { year?: string }) {
    const authModule = this.context.rootState.AuthModule as {
      user?: { id?: number; departmentId?: number; capDonVi?: number };
      workingYears?: number[];
    };

    const user = authModule?.user;
    if (!user?.id) {
      return;
    }

    const workingYears =
      authModule?.workingYears?.length
        ? authModule.workingYears
        : getFallbackWorkingYears();
    const year = clampYearToWorkingYears(payload?.year, workingYears);

    const settings = resolveUserSystemSettings(
      user.id,
      user.departmentId ?? 0,
      user.capDonVi ?? 2,
      year,
    );
    settings.year = clampYearToWorkingYears(settings.year, workingYears);

    this.context.commit(Mutations.SET_USER_SYSTEM_SETTINGS, settings);
    this.context.commit(Mutations.SET_YEAR, settings.year);
  }

  @Action
  [Actions.SAVE_USER_SYSTEM_SETTINGS](settings: UserSystemSettings) {
    const authModule = this.context.rootState.AuthModule as {
      user?: { id?: number; departmentId?: number; capDonVi?: number };
      workingYears?: number[];
    };

    const user = authModule?.user;
    if (!user?.id) {
      return;
    }

    const workingYears =
      authModule?.workingYears?.length
        ? authModule.workingYears
        : getFallbackWorkingYears();

    const normalizedSettings = {
      ...settings,
      year: clampYearToWorkingYears(settings.year, workingYears),
    };

    if (user.capDonVi !== 1) {
      normalizedSettings.idDonVi = user.departmentId ?? normalizedSettings.idDonVi;
    }

    saveUserSystemSettings(user.id, normalizedSettings);
    this.context.commit(Mutations.SET_USER_SYSTEM_SETTINGS, normalizedSettings);
    this.context.commit(Mutations.SET_YEAR, normalizedSettings.year);
  }
}
